import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchLyrics } from '../services/Lyricsapi';
import { selectCatalog, fetchBhajanDataRequest } from '../Features/BhajanSlice';
import { deriveBhajanDetail } from '../bhajan/deriveBhajanDetail';
import { useDetailNodes } from '../bhajan/useDetailNodes';
import { LyricsRenderer } from '../bhajan/LyricsRenderer';
import { FontSizeControl, MAX_LYRICS_FONT_SIZE, nextFontSize } from '../bhajan/FontSizeControl';
import { IconButton, cn } from '../ui';
import { getDistractionAlways, setDistractionAlways } from '../bhajan/distractionPref';
import type { RootState } from '../store/store';

const FONT_KEY = 'distractionFontSize';
/** Distraction mode starts bigger than the detail page but shares its ceiling. */
const MIN_FONT = 18;
const LANG_TO_CODE: Record<string, string> = {
  English: 'en', Telugu: 'te', Kannada: 'kn', Tamil: 'ta', Hindi: 'hi',
  Malayalam: 'ml', Oriya: 'or', Bengali: 'bn', Punjabi: 'pa', Gujarati: 'gu',
};

const Distraction: React.FC = () => {
  const { nodeId } = useParams<{ nodeId: string }>();
  const selectedLanguage = useSelector((s: RootState) => s.language.selectedLanguage);
  const language = LANG_TO_CODE[selectedLanguage] || 'en';
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const catalog = useSelector(selectCatalog);
  const [lyrics, setLyrics] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [fontSize, setFontSize] = useState<number>(() => {
    const v = parseInt(localStorage.getItem(FONT_KEY) || '', 10);
    return Number.isFinite(v) && v >= MIN_FONT && v <= MAX_LYRICS_FONT_SIZE ? v : 30;
  });
  const [showControls, setShowControls] = useState(true);
  const [alwaysOn, setAlwaysOn] = useState<boolean>(getDistractionAlways);
  const hideTimer = useRef<number | null>(null);

  // Name + bhajan id come from the catalog (same source as the detail page).
  useEffect(() => {
    if (!catalog) dispatch(fetchBhajanDataRequest());
  }, [catalog, dispatch]);

  const { nodes: detailNodes } = useDetailNodes(Number(nodeId));
  const vm = useMemo(
    () => deriveBhajanDetail(detailNodes, Number(nodeId), language),
    [detailNodes, nodeId, language]
  );
  const name = vm.title;
  const bhajanId = vm.bhajanId;

  // Lyrics text isn't in the catalog — fetch on demand (best-effort).
  useEffect(() => {
    if (!catalog || !vm.found) return;
    const lyricsId = vm.lyricsNodeId;
    if (!lyricsId) { setLyrics('Lyrics not available.'); setLoading(false); return; }

    let cancelled = false;
    setLoading(true);
    fetchLyrics(String(lyricsId), language)
      .then((ld) => { if (!cancelled) { setLyrics(ld.data?.text ?? 'Lyrics not available.'); setLoading(false); } })
      .catch(() => { if (!cancelled) { setLyrics('Could not load lyrics.'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [catalog, vm.found, vm.lyricsNodeId, language]);

  const onFontChange = useCallback((next: number) => {
    setFontSize(next);
    localStorage.setItem(FONT_KEY, String(next));
  }, []);

  const exit = useCallback(() => {
    // If preference is on, the bhajan detail would just bounce back here. Turn off when exiting.
    setDistractionAlways(false);
    setAlwaysOn(false);
    if (nodeId) navigate(`/nodes/${nodeId}`, { replace: true });
    else navigate('/bhajans');
  }, [nodeId, navigate]);

  // Wake Lock
  useEffect(() => {
    let sentinel: WakeLockSentinel | null = null;
    const acquire = async () => {
      try {
        if ('wakeLock' in navigator) sentinel = await navigator.wakeLock.request('screen');
      } catch { /* ignore */ }
    };
    acquire();
    const onVis = () => { if (document.visibilityState === 'visible' && !sentinel) void acquire(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      try { sentinel?.release?.(); } catch { /* ignore */ }
    };
  }, []);

  // Keyboard
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); exit(); return; }
      // Same ladder as the on-screen control, so +/- and the buttons agree.
      if (e.key === '+' || e.key === '=') onFontChange(nextFontSize(fontSize, 1, MIN_FONT, MAX_LYRICS_FONT_SIZE));
      if (e.key === '-' || e.key === '_') onFontChange(nextFontSize(fontSize, -1, MIN_FONT, MAX_LYRICS_FONT_SIZE));
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [exit, fontSize, onFontChange]);

  // Auto-hide controls after 4s of inactivity
  const bumpControls = useCallback(() => {
    setShowControls(true);
    if (hideTimer.current) window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setShowControls(false), 4000);
  }, []);
  useEffect(() => {
    bumpControls();
    const events: (keyof DocumentEventMap)[] = ['mousemove', 'touchstart', 'keydown'];
    events.forEach((e) => document.addEventListener(e, bumpControls));
    return () => {
      events.forEach((e) => document.removeEventListener(e, bumpControls));
      if (hideTimer.current) window.clearTimeout(hideTimer.current);
    };
  }, [bumpControls]);

  const handleClickWrapper = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button, a, input, [role="button"], [role="switch"], [data-no-exit]')) return;
    exit();
  };

  const onToggleAlways = () => {
    const next = !alwaysOn;
    setDistractionAlways(next);
    setAlwaysOn(next);
  };

  return (
    <div
      className="fixed inset-0 z-[1200] overflow-y-auto cursor-pointer"
      style={{ background: 'var(--bg-0)' }}
      onClick={handleClickWrapper}
      data-distraction-root
    >
      {/* faint star backdrop */}
      <svg className="pointer-events-none absolute inset-0 w-full h-full opacity-25" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
        {Array.from({ length: 60 }).map((_, i) => {
          const cx = ((i * 71) % 100) + (i % 7) * 0.3;
          const cy = ((i * 43) % 100) + (i % 5) * 0.2;
          const r = 0.1 + ((i * 13) % 5) * 0.05;
          return <circle key={i} cx={cx} cy={cy} r={r} fill="#ffffff" opacity={0.3 + ((i * 11) % 7) * 0.08} />;
        })}
      </svg>

      <div
        className={cn(
          'fixed top-3 right-3 z-[10] flex items-center gap-2 transition-opacity duration-400',
          showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        data-no-exit
      >
        <FontSizeControl value={fontSize} onChange={onFontChange} min={MIN_FONT} max={MAX_LYRICS_FONT_SIZE} />

        <label
          className="inline-flex items-center gap-2 rounded-full border border-light bg-bg-3/80 backdrop-blur px-3 h-9 text-small text-text-secondary cursor-pointer select-none"
          title="When on, every bhajan opens in distraction mode"
        >
          <input
            type="checkbox"
            role="switch"
            checked={alwaysOn}
            onChange={onToggleAlways}
            className="accent-accent-gold"
          />
          <span>Always on</span>
        </label>

        <IconButton
          ariaLabel="Exit distraction mode"
          variant="glass"
          onClick={exit}
          icon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="18" x2="18" y2="6" />
            </svg>
          }
        />
      </div>

      <div className="relative min-h-screen mx-auto max-w-3xl px-4 sm:px-8 py-32">
        {bhajanId != null && (
          <div className="font-mono text-small text-text-tertiary tabular-nums mb-3" data-no-exit>
            #{bhajanId}
          </div>
        )}
        {name && (
          <h1
            className="font-display font-semibold text-text-primary mb-10 break-words"
            style={{ fontSize: Math.max(fontSize * 0.9, 24) }}
          >
            {name}
          </h1>
        )}
        <div className="w-full" data-no-exit>
          <LyricsRenderer
            text={lyrics}
            isLoading={loading && !lyrics}
            fontSize={fontSize}
            align="left"
            languageCode={language}
          />
        </div>
        <p className="mt-16 text-small text-text-tertiary opacity-60" data-no-exit>
          tap anywhere · esc to exit
        </p>
      </div>
    </div>
  );
};

export default Distraction;
